import React, { createContext, useContext, useState, ReactNode } from "react";
import { CaseItem, Filter, Sort } from "../types/CaseListTypes";
import {
  getUserProfileTokenName,
  STORAGE_KEY,
} from "../components/shared/helpers/helper";

interface CaseListContextType {
  // Search state
  searchValue: string;
  setSearchValue: (value: string) => void;

  // Filter state
  filters: Filter[];
  setFilters: (filters: Filter[]) => void;
  pendingFilters: Filter[]; // Filters being edited in drawer
  setPendingFilters: (filters: Filter[]) => void;

  filter: Filter;
  setFilter: (filter: Filter) => void;
  filterClicked: boolean;
  setFilterClicked: (clicked: boolean) => void;
  from: string | null;
  setFrom: (date: string | null) => void;
  to: string | null;
  setTo: (date: string | null) => void;

  // Sort state
  sort: Sort;
  setSort: (sort: Sort) => void;
  sortClicked: boolean;
  setSortClicked: (clicked: boolean) => void;

  // List state
  list: CaseItem[];
  setList: (list: CaseItem[]) => void;

  // Pagination state
  page: number;
  setPage: (page: number) => void;
  noOfPages: number;
  setnoOfPages: (pages: number) => void;

  // Loading state
  caseLoader: boolean;
  setCaseLoader: (loading: boolean) => void;
  loaderState: boolean;
  setLoaderState: (loading: boolean) => void;

  // View state
  caseView: "grid" | "table";
  setCaseView: (view: "grid" | "table") => void;

  // Reset function
  resetToDefaults: () => void;
  getDefaultFilters: () => Filter[];
}

const CaseListContext = createContext<CaseListContextType | undefined>(
  undefined,
);

export const CaseListProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  // Search state
  const [searchValue, setSearchValue] = useState("");

  // Filter state
  const [filters, setFilters] = useState<Filter[]>([]);
  const [pendingFilters, setPendingFilters] = useState<Filter[]>([]);
  const [filter, setFilter] = useState<Filter>({ type: "status", value: null });

  const [filterClicked, setFilterClicked] = useState(false);
  const [from, setFrom] = useState<string | null>(null);
  const [to, setTo] = useState<string | null>(null);

  // Sort state
  const [sort, setSort] = useState<Sort>({ value: null, type: "ASC" });
  const [sortClicked, setSortClicked] = useState(false);

  // List state
  // const [list, setList] = useState<any[] | null>(null);
  const [list, setList] = useState<CaseItem[]>([]);

  // Pagination state
  const [page, setPage] = useState(1);
  const [noOfPages, setnoOfPages] = useState(0);

  // Loading state
  const [caseLoader, setCaseLoader] = useState(true);
  const [loaderState, setLoaderState] = useState(true);

  // View state
  const [caseView, setCaseView] = useState<"grid" | "table">("grid");

  const getDefaultFilters = (): Filter[] => {
    const userInfo = JSON.parse(
      localStorage.getItem(getUserProfileTokenName()) || "{}",
    );

    const defaultFilters: Filter[] = [
      { type: "select", key: "status", value: "active" },
    ];

    if (userInfo.user_name) {
      defaultFilters.push({
        type: "select",
        key: "user",
        value: userInfo.user_name,
      });
    }

    return defaultFilters;
  };

  // Function to reset to default filters
  const resetToDefaults = () => {
    localStorage.removeItem(STORAGE_KEY);
    const defaultFilters = getDefaultFilters();
    setFilters(defaultFilters);
    setSort({ value: null, type: "ASC" });
    setSearchValue("");
    setPage(1);
    setList([]);
    setnoOfPages(0);
  };

  const value: CaseListContextType = {
    searchValue,
    setSearchValue,
    filters,
    setFilters,
    pendingFilters,
    setPendingFilters,
    filter,
    setFilter,
    filterClicked,
    setFilterClicked,
    from,
    setFrom,
    to,
    setTo,
    sort,
    setSort,
    sortClicked,
    setSortClicked,
    list,
    setList,
    page,
    setPage,
    noOfPages,
    setnoOfPages,
    caseLoader,
    setCaseLoader,
    loaderState,
    setLoaderState,
    caseView,
    setCaseView,
    resetToDefaults,
    getDefaultFilters,
  };

  return (
    <CaseListContext.Provider value={value}>
      {children}
    </CaseListContext.Provider>
  );
};

export const useCaseListContext = () => {
  const context = useContext(CaseListContext);
  if (context === undefined) {
    throw new Error(
      "useCaseListContext must be used within a CaseListProvider",
    );
  }
  return context;
};
